<template>
  <section class="trp" :class="{ 'trp--narrow': narrow }" data-test="task-reports" aria-labelledby="trp-title">
    <div class="trp__inner">
      <button type="button" class="trp__back" data-test="task-reports-back" @click="$emit('back')">
        <tr-icon name="chevL" :size="16" :stroke="2.2" />Tasks
      </button>

      <header class="trp__head">
        <div class="trp__intro">
          <h1 id="trp-title" class="trp__title">Task reports</h1>
          <p class="trp__lede" data-test="task-reports-lede">{{ lede }}</p>
        </div>
        <button type="button" class="trp__btn" data-test="task-reports-export" :disabled="busy" @click="exportCsv">
          <tr-icon name="download" :size="16" />Export CSV
        </button>
      </header>

      <div class="trp__filters" data-test="task-reports-filters">
        <div class="trp__pills" role="group" aria-label="Date range">
          <button
            v-for="p in presets"
            :key="p.key"
            type="button"
            class="trp__pill"
            :class="{ 'is-on': p.key === preset }"
            :aria-pressed="p.key === preset ? 'true' : 'false'"
            data-test="task-reports-range"
            :data-range="p.key"
            @click="setPreset(p.key)"
          >{{ p.label }}</button>
        </div>
        <div v-if="preset === 'custom'" class="trp__custom" data-test="task-reports-custom">
          <input
            type="date"
            class="trp__date"
            aria-label="From date"
            data-test="task-reports-from"
            :value="customFrom"
            :max="customTo || null"
            @change="onDate('customFrom', $event)"
          />
          <span class="trp__to">to</span>
          <input
            type="date"
            class="trp__date"
            aria-label="To date"
            data-test="task-reports-to"
            :value="customTo"
            :min="customFrom || null"
            @change="onDate('customTo', $event)"
          />
        </div>
        <select v-model="tag" class="trp__tag" aria-label="Tag" data-test="task-reports-tag">
          <option value="">All tags</option>
          <option v-for="t in tags" :key="t" :value="t">#{{ t }}</option>
        </select>
        <span class="trp__cmp" data-test="task-reports-compare">{{ compareText }}</span>
      </div>

      <div v-if="busy" class="trp__skeleton" data-test="task-reports-loading" aria-hidden="true">
        <div class="trp__skel-cards"><span v-for="n in 4" :key="n" class="trp__skel-card"></span></div>
        <span v-for="n in 5" :key="`r${n}`" class="trp__skel-row"></span>
      </div>

      <template v-else>
        <div class="trp__cards" data-test="task-reports-cards">
          <button
            v-for="c in cards"
            :key="c.key"
            type="button"
            class="trp__card"
            :class="{ 'is-on': sortKey === c.sortKey }"
            :title="c.tip"
            :aria-pressed="sortKey === c.sortKey ? 'true' : 'false'"
            data-test="task-reports-card"
            :data-card="c.key"
            @click="sortBy(c.sortKey, c.sortDir)"
          >
            <span class="trp__card-label">{{ c.label }}</span>
            <span class="trp__card-valrow">
              <span class="trp__card-value" data-test="task-reports-card-value">{{ c.value }}</span>
              <span v-if="c.delta" class="trp__card-delta" :class="`is-${c.trend}`" data-test="task-reports-card-delta">{{ c.delta }}</span>
            </span>
            <span class="trp__card-sub" data-test="task-reports-card-sub">{{ c.sub }}</span>
          </button>
        </div>

        <div class="trp__team" data-test="task-reports-team">
          <div class="trp__team-head">
            <span class="trp__team-title">Team</span>
            <span class="trp__team-sub" data-test="task-reports-team-sub">{{ teamText }}</span>
            <div class="trp__legend" data-test="task-reports-legend">
              <span v-for="s in segments" :key="s.key" class="trp__legend-item">
                <span class="trp__swatch" :class="`trp__swatch--${s.key}`"></span>{{ s.label }}
              </span>
            </div>
          </div>

          <div class="trp__table" role="table" aria-label="Team timeliness">
            <div class="trp__tr trp__tr--head" role="row">
              <template v-for="col in columns">
                <button
                  v-if="col.key"
                  :key="col.label"
                  type="button"
                  role="columnheader"
                  class="trp__th"
                  :class="{ 'is-num': col.num, 'is-on': sortKey === col.key }"
                  :aria-sort="sortKey === col.key ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'"
                  data-test="task-reports-col"
                  :data-col="col.key"
                  @click="sortBy(col.key)"
                >
                  {{ col.label }}<tr-icon v-if="sortKey === col.key" :name="sortDir === 'asc' ? 'up' : 'down'" :size="12" :stroke="2.4" />
                </button>
                <span v-else :key="col.label" role="columnheader" class="trp__th is-static">{{ col.label }}</span>
              </template>
            </div>

            <div class="trp__tbody">
              <div
                v-for="r in rows"
                :key="r.empID"
                class="trp__tr"
                role="row"
                data-test="task-reports-row"
                :data-emp="r.empID"
              >
                <div class="trp__who" role="cell">
                  <span class="trp__avatar" aria-hidden="true">{{ r.initials }}</span>
                  <span class="trp__name" :title="r.name" data-test="task-reports-name">{{ r.name }}</span>
                </div>
                <div class="trp__bar" role="cell" :title="r.barTitle" :aria-label="r.barTitle" data-test="task-reports-bar">
                  <span
                    v-for="g in r.segments"
                    :key="g.key"
                    class="trp__seg"
                    :class="`trp__seg--${g.key}`"
                    :style="{ width: `calc(${g.pct}% - 2px)` }"
                    :title="g.tip"
                    :data-seg="g.key"
                  ></span>
                </div>
                <div class="trp__num" role="cell" data-col="due"><span class="trp__num-label">Due</span>{{ r.due }}</div>
                <div class="trp__num" role="cell" data-col="done"><span class="trp__num-label">Done</span>{{ r.done }}</div>
                <div class="trp__num is-strong" :class="{ 'is-alert': r.onTimeAlert, 'is-none': r.onTimePct === null }" role="cell" data-col="onTimePct">
                  <span class="trp__num-label">On time</span>{{ r.onTimeText }}
                </div>
                <div class="trp__num is-muted" :class="{ 'is-none': r.avgLate === null }" role="cell" data-col="avgLate">
                  <span class="trp__num-label">Avg late</span>{{ r.avgLateText }}
                </div>
                <div class="trp__num" :class="r.openAlert ? 'is-alert' : 'is-muted'" role="cell" data-col="open">
                  <span class="trp__num-label">Open</span>{{ r.open }}
                </div>
              </div>
            </div>
            <p v-if="!rows.length" class="trp__empty" data-test="task-reports-empty">{{ emptyText }}</p>
          </div>

          <p class="trp__foot">On time means completed on or before the due date. Average days late counts completed tasks only.</p>
        </div>
      </template>
    </div>
  </section>
</template>

<script>
/**
 * Task reports — the redesigned page (design "Task Reports & Settings" 1c, 2026-10-07), shown INSIDE
 * the new Tasks page (ToDo.vue `tkScreen === 'reports'`, `?screen=reports`) in place of the list.
 * Plan: C:\Users\nilay\.claude\plans\shiny-gliding-moth.md.
 *
 * Presentational: no store, no route. ToDo.vue hands it the site's tasks already NORMALISED
 * (taskReport.js normalizeTask — office date format and zone), the employees, the site's tags and
 * the office's today. Every number comes from src/utils/tasks/taskReport.js (pinned by
 * cypress/node-tests/tasks-report-rules.test.js); this file owns only the filter / sort UI state,
 * the CSV download (Blob + a hidden <a download>) and the toast after it.
 *
 * One table replaces the classic panel's scorecard, per-person pies and bar chart. Clicking a number
 * card or a column header sorts the table. Below a 760px CONTAINER the rows stack (`.trp--narrow`).
 * Emits `back` (the "← Tasks" link).
 */
import {
  RANGE_PRESETS, SEGMENTS, ON_TIME_ALERT_PCT, OPEN_ALERT, SORT_KEYS, rangeFor, previousRange, buildReport,
  segmentsOf, statCards, sortRows, tagOptions, csv,
} from '@/utils/tasks/taskReport.js';

const NARROW_PX = 760;

// Lucide path data (ISC/MIT, as FuIcon.vue) on a 24×24 viewBox, stroked in currentColor.
const ICONS = {
  chevL: ['m15 18-6-6 6-6'],
  download: ['M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4', 'm7 10 5 5 5-5', 'M12 15V3'],
  up: ['M12 19V5', 'M5 12l7-7 7 7'],
  down: ['M12 5v14', 'M19 12l-7 7-7-7'],
};

const TrIcon = {
  name: 'TrIcon',
  functional: true,
  props: {
    name: { type: String, required: true },
    size: { type: [Number, String], default: 16 },
    stroke: { type: [Number, String], default: 1.9 },
  },
  render(h, { props, data }) {
    return h('svg', {
      staticClass: ['trp__icon', data.staticClass].filter(Boolean).join(' '),
      attrs: {
        width: props.size, height: props.size, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor',
        'stroke-width': props.stroke, 'stroke-linecap': 'round', 'stroke-linejoin': 'round',
        'aria-hidden': 'true', focusable: 'false',
      },
    }, (ICONS[props.name] || []).map(d => h('path', { attrs: { d } })));
  },
};

const COLUMNS = [
  { key: 'name', label: 'Teammate' },
  { key: null, label: 'Timeliness' },
  { key: 'due', label: 'Due', num: true },
  { key: 'done', label: 'Done', num: true },
  { key: 'onTimePct', label: 'On time', num: true },
  { key: 'avgLate', label: 'Avg late', num: true },
  { key: 'open', label: 'Open', num: true },
];

/** "Maya Rivera" → "MR". */
function initialsOf(name) {
  const words = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!words.length) return '?';
  const first = words[0][0] || '';
  const last = words.length > 1 ? (words[words.length - 1][0] || '') : '';
  return (first + last).toUpperCase();
}

export default {
  name: 'TaskReportsPage',
  components: { TrIcon },
  props: {
    // taskReport.js normalizeTask items: { id, empID, dueYmd, doneYmd, done, deleted, tags }
    items: { type: Array, default: () => [] },
    // state.customer.employees (EmployeeID, Name)
    employees: { type: Array, default: () => [] },
    siteTags: { type: Array, default: () => [] },
    // the office's today, 'YYYY-MM-DD'
    todayYmd: { type: String, required: true },
    // the site's tasks are still loading (named `busy`, not `loading` — src/mixin.js shadows `loading`)
    busy: { type: Boolean, default: false },
  },
  data() {
    return {
      preset: 'thisWeek',
      customFrom: '',
      customTo: '',
      tag: '',
      sortKey: 'due',
      sortDir: 'desc',
      narrow: false,
      ro: null,
    };
  },
  computed: {
    presets() {
      return RANGE_PRESETS;
    },
    segments() {
      return SEGMENTS;
    },
    columns() {
      return COLUMNS;
    },
    range() {
      return rangeFor(this.preset, this.todayYmd, { from: this.customFrom, to: this.customTo });
    },
    prevRange() {
      return previousRange(this.range);
    },
    report() {
      const r = this.range;
      if (!r) return { rows: [], totals: null };
      return buildReport(this.items, { from: r.from, to: r.to, tag: this.tag, todayYmd: this.todayYmd });
    },
    prevReport() {
      const r = this.prevRange;
      if (!r) return { rows: [], totals: null };
      return buildReport(this.items, { from: r.from, to: r.to, tag: this.tag, todayYmd: this.todayYmd });
    },
    cards() {
      return statCards(this.report.totals, this.prevReport.totals);
    },
    /** empID → display name. '' = a task assigned to no one; an id not on the roster = a former teammate. */
    names() {
      const out = {};
      const emps = Array.isArray(this.employees) ? this.employees : [];
      this.report.rows.forEach((r) => {
        if (r.empID === '') {
          out[r.empID] = 'No one assigned';
          return;
        }
        const e = emps.find(x => x && String(x.EmployeeID) === r.empID);
        out[r.empID] = e && e.Name ? String(e.Name) : 'Former teammate';
      });
      return out;
    },
    rows() {
      const named = this.report.rows.map(r => Object.assign({}, r, { name: this.names[r.empID] }));
      return sortRows(named, this.sortKey, this.sortDir).map((r) => {
        const notDue = r.notDue ? ` · ${r.notDue} not due yet` : '';
        return Object.assign({}, r, {
          initials: r.empID === '' ? '–' : initialsOf(r.name),
          segments: segmentsOf(r),
          barTitle: `${r.done} of ${r.due} done${notDue}`,
          onTimeText: r.onTimePct === null ? '—' : `${r.onTimePct}%`,
          onTimeAlert: r.onTimePct !== null && r.onTimePct < ON_TIME_ALERT_PCT,
          avgLateText: r.avgLate === null ? '—' : `${(Math.round(r.avgLate * 10) / 10).toFixed(1)} d`,
          openAlert: r.open > OPEN_ALERT,
        });
      });
    },
    tags() {
      return tagOptions(this.items, this.siteTags);
    },
    lede() {
      const r = this.range;
      if (!r) return '';
      return `How your team kept up with tasks ${r.text}${this.tag ? `, tagged #${this.tag}` : ''}.`;
    },
    compareText() {
      return this.prevRange ? this.prevRange.text : '';
    },
    teamText() {
      const n = this.rows.length;
      const t = this.report.totals ? this.report.totals.due : 0;
      return `${n} ${n === 1 ? 'person' : 'people'} · ${t} ${t === 1 ? 'task' : 'tasks'}`;
    },
    emptyText() {
      return this.tag ? `No tasks tagged #${this.tag} were due in this period.` : 'No tasks were due in this period.';
    },
  },
  mounted() {
    this.measure();
    if (typeof window !== 'undefined' && typeof window.ResizeObserver === 'function') {
      this.ro = new window.ResizeObserver(() => this.measure());
      this.ro.observe(this.$el);
    } else if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.measure);
    }
  },
  beforeDestroy() {
    if (this.ro) this.ro.disconnect();
    this.ro = null;
    if (typeof window !== 'undefined') window.removeEventListener('resize', this.measure);
  },
  methods: {
    measure() {
      const w = this.$el ? this.$el.clientWidth : 0;
      if (w > 0) this.narrow = w < NARROW_PX;
    },
    setPreset(key) {
      if (key === 'custom' && (!this.customFrom || !this.customTo)) {
        const cur = this.range;
        if (cur) {
          this.customFrom = cur.from;
          this.customTo = cur.to;
        }
      }
      this.preset = key;
    },
    onDate(field, e) {
      const v = e && e.target ? e.target.value : '';
      if (v) this[field] = v;
    },
    /** A column click toggles the direction on the sorted column; a card (or a new column) sets it. */
    sortBy(key, dir) {
      if (!key) return;
      if (!dir && this.sortKey === key) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
        return;
      }
      this.sortKey = key;
      this.sortDir = dir || SORT_KEYS[key] || 'desc';
    },
    exportCsv() {
      const out = csv(this.rows, this.names, this.range, this.report.totals);
      try {
        const blob = new Blob([out.text], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = out.filename;
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => {
          try { URL.revokeObjectURL(url); } catch (e) { /* already gone */ }
        }, 1000);
      } catch (err) {
        console.error('[Task reports] CSV export failed:', err);
        if (this.$toast && typeof this.$toast.error === 'function') this.$toast.error('The report could not be downloaded. Please try again.');
        return;
      }
      if (this.$toast && typeof this.$toast.success === 'function') this.$toast.success(`Downloaded ${out.filename}`);
      this.$emit('exported', out.filename);
    },
  },
};
</script>

<style src="../task-reports.scss" lang="scss" scoped />
